import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ArrowUpRight, Camera } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { Reveal, SectionLabel } from "@/components/Reveal";
import { r3Services, r3Imgs, r3Equipment } from "@/data/content";
import { useBusinessInfo } from "@/lib/useBusinessInfo";

export const R3Services = () => {
  const { t } = useLanguage();
  const biz = useBusinessInfo();
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section
      id="r3"
      data-testid="r3-section"
      className="border-y border-white/10 bg-navy py-24 md:py-36"
    >
      <div className="mx-auto max-w-[1600px] px-5 md:px-10">
        <div className="grid gap-12 md:grid-cols-12 md:gap-16">
          <div className="md:col-span-5">
            <Reveal>
              <SectionLabel text={t.r3.label} testId="r3-label" />
              <h2
                data-testid="r3-heading"
                className="mt-6 font-display text-5xl font-black uppercase leading-[0.92] tracking-tight text-white md:text-7xl"
              >
                R3
                <br />
                <span className="text-gold">Services</span>
              </h2>
              <p className="mt-6 text-sm font-bold uppercase tracking-[0.2em] text-neutral-300 md:text-base">
                {t.r3.tagline}
              </p>
              <p className="mt-4 max-w-md text-base leading-relaxed text-neutral-400">
                {t.r3.sub}
              </p>
              <Link
                data-testid="r3-cta"
                to="/request-a-bid"
                className="group mt-9 inline-flex items-center gap-2 rounded-full bg-gold px-8 py-4 text-sm font-black uppercase tracking-[0.12em] text-ink transition-colors duration-300 hover:bg-golddark"
              >
                {t.r3.cta}
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </Link>
              <div
                data-testid="r3-contact-card"
                className="mt-8 border border-white/10 bg-[#0c1425] p-6 md:p-7"
              >
                <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-neutral-500">
                  {t.r3.contactLabel}
                </p>
                <p className="mt-2 font-display text-2xl font-black tracking-tight text-white">
                  {biz.r3ContactName}
                </p>
                <div className="mt-4 flex flex-wrap gap-3">
                  <a
                    data-testid="r3-call-btn"
                    href={biz.phone2Href}
                    className="flex items-center gap-2 rounded-full bg-gold px-5 py-3 text-xs font-black uppercase tracking-[0.12em] text-ink transition-colors duration-300 hover:bg-golddark"
                  >
                    <Phone className="h-3.5 w-3.5" />
                    {t.contact.call} · {biz.phone2}
                  </a>
                  <a
                    data-testid="r3-text-btn"
                    href={biz.sms2Href}
                    className="flex items-center gap-2 rounded-full border border-white/20 px-5 py-3 text-xs font-bold uppercase tracking-[0.12em] text-white transition-colors duration-300 hover:border-gold hover:text-gold"
                  >
                    {t.contact.text} · {biz.phone2}
                  </a>
                </div>
                <p className="mt-4 text-xs leading-relaxed text-neutral-500">{t.r3.drywallNote}</p>
              </div>
            </Reveal>

            <div ref={ref} className="relative mt-14 hidden md:block">
              <motion.div
                style={{ y: imgY }}
                className="relative aspect-[16/10] w-4/5 overflow-hidden border border-white/10"
              >
                <img
                  src={r3Imgs[0]}
                  alt="R3 Services equipment clearing a property"
                  loading="lazy"
                  className="h-full w-full object-cover"
                />
              </motion.div>
              <div className="absolute -bottom-10 right-0 aspect-square w-2/5 overflow-hidden border-4 border-navy">
                <img
                  src={r3Imgs[1]}
                  alt="Excavator on an R3 Services job site"
                  loading="lazy"
                  className="h-full w-full object-cover"
                />
              </div>
            </div>
          </div>

          <div className="md:col-span-7">
            <div className="grid gap-px overflow-hidden border border-white/10 bg-white/10 sm:grid-cols-2">
              {r3Services.map((s, i) => {
                const Icon = s.icon;
                const copy = t.r3.items[s.slug];
                return (
                  <Reveal key={s.slug} delay={(i % 2) * 0.08} className="bg-navy">
                    <div
                      data-testid={`r3-card-${s.slug}`}
                      className="group flex h-full flex-col p-7 transition-colors duration-500 hover:bg-[#131c33] md:p-9"
                    >
                      <div className="flex items-center justify-between">
                        <Icon className="h-6 w-6 text-gold" strokeWidth={1.5} />
                        <span className="text-[10px] font-bold tracking-[0.2em] text-neutral-600">
                          R3·0{i + 1}
                        </span>
                      </div>
                      <h3 className="mt-7 font-display text-lg font-black uppercase tracking-tight text-white md:text-xl">
                        {copy.title}
                      </h3>
                      <p className="mt-2.5 text-sm leading-relaxed text-neutral-400">
                        {copy.text}
                      </p>
                      <span className="mt-auto block pt-6">
                        <span className="block h-[2px] w-8 bg-gold transition-[width] duration-500 group-hover:w-full" />
                      </span>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </div>

        <div className="mt-20 md:mt-28">
          <Reveal>
            <SectionLabel text={t.r3.equipment.label} testId="r3-equipment-label" />
            <p
              data-testid="r3-equipment-note"
              className="mt-3 text-xs font-bold uppercase tracking-[0.2em] text-neutral-500"
            >
              {t.r3.equipment.note}
            </p>
          </Reveal>
          <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {r3Equipment.map((item, i) => (
              <Reveal key={item.slug} delay={(i % 4) * 0.08}>
                {item.img ? (
                  <figure
                    data-testid={`r3-equipment-${item.slug}`}
                    className="group relative aspect-[4/3] overflow-hidden border border-white/10"
                  >
                    <img
                      src={item.img}
                      alt={`${t.r3.equipment.items[item.slug]} — R3 Services, Houston`}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                    <figcaption className="absolute inset-x-0 bottom-0 bg-ink/70 px-4 py-3 text-[11px] font-bold uppercase tracking-[0.15em] text-white backdrop-blur-md">
                      {t.r3.equipment.items[item.slug]}
                    </figcaption>
                  </figure>
                ) : (
                  <div
                    data-testid={`r3-equipment-${item.slug}`}
                    className="flex aspect-[4/3] flex-col items-center justify-center gap-3 border border-dashed border-white/15 bg-[#0c1425] p-6 text-center"
                  >
                    <Camera className="h-7 w-7 text-neutral-600" strokeWidth={1.5} />
                    <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-300">
                      {t.r3.equipment.items[item.slug]}
                    </p>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-neutral-600">
                      {t.r3.equipment.comingSoon}
                    </p>
                  </div>
                )}
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
